import React, { useState, useEffect } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Controller, Control, RegisterOptions } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { UserAPI } from 'api';
import { DeepMap } from 'react-hook-form/dist/types/utils';
import { Role } from 'models/Role';
import { Typography, makeStyles, Box, Chip } from '@material-ui/core';
import UserAvatar from './UserAvatar';

interface Props {
  control: Control;
  name: string;
  rules: RegisterOptions;
  errors: DeepMap<any, any>;
  label: string;
  filterRoles?: Array<Role>;
  className?: string;
  startAdornment?: any;
  disabled?: boolean;
  filterLimitedUsers?: boolean;
}

const useStyles = makeStyles((theme) => ({
  option: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(0.6),
    paddingBottom: theme.spacing(0.6),
  },
  avatar: {
    width: theme.spacing(3),
    height: theme.spacing(3),
    fontSize: '13px',
    marginRight: theme.spacing(2),
  },
}));

/** Provides the selected user ID */
const UserAutocomplete = ({
  control,
  name,
  rules,
  errors,
  label,
  filterRoles,
  className,
  disabled = false,
  filterLimitedUsers = false,
}: Props) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { isFetching, data: users } = useQuery(
    ['list-users'],
    UserAPI.listUsers,
    { staleTime: 30 * 60 * 1000 }
  );

  const [filteredUsers, setFilteredUsers] = useState([]);
  useEffect(() => {
    if (!users) {
      return;
    }
    if (!filterRoles) {
      if (filterLimitedUsers) {
        setFilteredUsers(users.filter((u) => u?.isLimitedUser));
      } else {
        setFilteredUsers(users);
      }
      return;
    }
    if (filterLimitedUsers) {
      setFilteredUsers(
        users.filter((u) => filterRoles.includes(u.role) && u?.isLimitedUser)
      );
      return;
    }
    setFilteredUsers(users.filter((u) => filterRoles.includes(u.role)));
  }, [users, filterRoles, filterLimitedUsers]);

  return (
    <Controller
      data-cy="client-project-manager"
      name={name}
      rules={rules}
      control={control}
      render={(props) => (
        <Autocomplete
          data-cy="client-project-manager"
          {...props}
          autoHighlight
          open={open}
          disabled={disabled}
          openOnFocus
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          options={isFetching ? [] : filteredUsers}
          noOptionsText="No matching users"
          loadingText="Loading Users..."
          loading={open && isFetching}
          getOptionLabel={(option) => {
            return `${option.firstName} ${option.lastName}`;
          }}
          renderOption={(option) => {
            if (!option) return undefined;
            return (
              <div className={classes.option}>
                <UserAvatar
                  disableTooltip
                  user={option}
                  size={24}
                  fontSize={'12px'}
                  className={classes.avatar}
                />
                <Box display="block">
                  <Typography variant="body1">
                    {option?.firstName} {option?.lastName} {''}
                    {option?.role && (
                      <Chip
                        variant="default"
                        style={{ marginLeft: 2 }}
                        size="small"
                        label={
                          <Typography variant="caption">
                            {option.role}
                          </Typography>
                        }
                      />
                    )}{' '}
                    {''}
                    {option?.isLimitedUser && (
                      <Chip
                        variant="outlined"
                        style={{ marginLeft: 2 }}
                        size="small"
                        label={
                          <Typography variant="caption">
                            Limited User
                          </Typography>
                        }
                      />
                    )}
                  </Typography>
                  <Typography variant="body2">{option?.email}</Typography>
                </Box>
              </div>
            );
          }}
          autoSelect
          getOptionSelected={(option, value) => {
            return option?.id === value?.id;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              fullWidth
              variant="outlined"
              className={className}
              error={!!errors[name]}
              helperText={!!errors[name] && errors[name].message}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {open && isFetching && (
                      <CircularProgress color="inherit" size={20} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          onChange={(_, data) => props.onChange(data)}
        />
      )}
    />
  );
};

export default UserAutocomplete;
